/**
 * Single-question form business case.
 *
 * Some pages embed `useForm` in a "single question" mode where the form
 * acts as a teaser: only the first step is shown, and submitting the
 * answer should redirect the user to another URL (built from the
 * domain's `routeConfig` placeholders) instead of going through the
 * normal multi-step / lead-submission flow.
 *
 * Owns:
 *   - The `isSingleQuestion` flag (just an alias of the prop, for
 *     readability at call sites).
 *   - The redirect itself, including:
 *       * setting `loadingForm` + the `isFormSubmitted` ref,
 *       * resolving `[keyword]` / `[generic-route]` placeholders from
 *         `routeConfig`,
 *       * forwarding the current query string (minus `slug`),
 *       * tracking a `sourceLink` click via `stackAction` when present,
 *       * persisting `skip-to-step` to sessionStorage.
 *
 * Note: the `singleQuestion` flag is also consumed by `useSteps` (to
 * slice to the first step) and by field-rendering logic — those are
 * presentation concerns and stay where they are.
 */

import { useCallback, MutableRefObject } from "react";
import { useRouter } from "next/router";

import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { replacePlaceholders } from "@lib/sharedUtils";
import { stackAction } from "@services/actionsTracker";

import { useDomainContext } from "@hooks/useDomainContext";
import { ActionTypes, FormReducerAction } from "../types";

interface UseSingleQuestionParams {
    singleQuestion?: boolean;
    urlToRedirectFromSingleQuestion?: string;
    dispatch: (action: FormReducerAction) => void;
    isFormSubmitted: MutableRefObject<boolean>;
}

interface RedirectOptions {
    skipToStep?: string;
}

export const useSingleQuestion = ({
    singleQuestion,
    urlToRedirectFromSingleQuestion,
    dispatch,
    isFormSubmitted,
}: UseSingleQuestionParams) => {
    const { query } = useRouter();
    const { routeConfig } = useDomainContext();

    const redirectForSingleQuestion = useCallback(
        (options?: RedirectOptions) => {
            dispatch({
                type: ActionTypes.SET_LOADING_FORM,
                payload: true,
            });
            isFormSubmitted.current = true;

            const genericRoute = routeConfig?.replacedPortions?.find(
                (portion) => portion.key === "[generic-route]",
            )?.value;
            const keyword = routeConfig?.replacedPortions?.find(
                (portion) => portion.key === "[keyword]",
            )?.value;

            const href = replacePlaceholders(
                urlToRedirectFromSingleQuestion?.trim() || "/",
                {
                    keyword,
                    genericRoute,
                    queryString: gettingQueriesAsString(query, "inline", [
                        "slug",
                    ]),
                },
            );

            const sourceLink =
                new URLSearchParams(href.split("?")[1]).get("sourceLink") || "";
            if (sourceLink) {
                stackAction({
                    action: "click",
                    category: "link",
                    label: "sourceLink",
                    value: sourceLink,
                    createdAt: new Date().toISOString(),
                });
            }

            if (options?.skipToStep) {
                sessionStorage.setItem("skip-to-step", options.skipToStep);
            }

            window.location.href = href;
        },
        [
            dispatch,
            isFormSubmitted,
            query,
            routeConfig,
            urlToRedirectFromSingleQuestion,
        ],
    );

    return {
        isSingleQuestion: !!singleQuestion,
        redirectForSingleQuestion,
    };
};
